import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const HERO_VIDEO =
  "https://assets.mixkit.co/videos/43482/43482-1080.mp4";
const HERO_POSTER =
  "https://assets.mixkit.co/videos/43482/43482-thumb-360-0.jpg";

const HEADLINE_LINES = [
  { id: "l1", node: "THE DIGITAL HOME" },
  {
    id: "l2",
    node: (
      <>
        OF <span className="text-[#26945D]">GRASSROOTS</span>
      </>
    ),
  },
  { id: "l3", node: "FOOTBALL" },
];

const EASE = [0.16, 1, 0.3, 1];
const INTRO_DELAY = 0.55;

function MaskedLine({ children, index }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{
          duration: 1.05,
          ease: EASE,
          delay: INTRO_DELAY + index * 0.12,
        }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden grain flex items-center"
    >
      {/* Parallax cinematic video background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        initial={{ scale: 1.18, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: EASE }}
        className="absolute inset-0 z-0"
      >
        <video
          data-testid="hero-video"
          className="h-full w-full object-cover object-center"
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
      </motion.div>

      {/* Overlays for contrast */}
      <div className="absolute inset-0 z-10 bg-[#0B0B0B]/35" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/35 to-[#0B0B0B]/15" />

      {/* Content — centrally aligned */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-20 w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-24 pb-36 flex flex-col items-start text-left"
      >
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          data-testid="hero-eyebrow"
          className="mb-8 flex items-center gap-3"
        >
          <span className="h-2 w-2 rounded-full bg-[#1F7A4D]" />
          <span className="font-body text-xs md:text-sm font-medium tracking-[0.28em] uppercase text-[#B3B3B3]">
            Grassroots &middot; Community &middot; Culture
          </span>
        </motion.div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="font-display uppercase text-white text-7xl sm:text-8xl md:text-[8rem] lg:text-[9.5vw] leading-[0.8] tracking-[0.005em]"
        >
          {HEADLINE_LINES.map((line, i) => (
            <MaskedLine key={line.id} index={i}>
              {line.node}
            </MaskedLine>
          ))}
        </h1>

        {/* Supporting copy */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: INTRO_DELAY + 0.55 }}
          data-testid="hero-supporting-copy"
          className="mt-9 max-w-[620px] font-body text-lg md:text-xl font-light leading-relaxed text-[#B3B3B3]"
        >
          More than a platform. A home where players, football communities,
          clubs and academies come together to grow the game from the ground up.
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: INTRO_DELAY + 0.72 }}
          className="mt-11 flex flex-wrap items-center justify-start gap-5"
        >
          <a
            href="#kick-off"
            data-testid="hero-cta-kick-off"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#1F7A4D] px-8 py-4 font-body text-base font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-[#26945D] hover:scale-[1.03] active:scale-100"
          >
            <span>⚽ Kick Off With Us</span>
            <ArrowUpRight
              size={20}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll cue — bottom center */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-10 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-3"
        data-testid="hero-scroll-cue"
      >
        <span className="font-body text-xs tracking-[0.25em] uppercase text-[#B3B3B3]/70">
          Scroll to explore
        </span>
        <span className="flex h-9 w-5 items-start justify-center rounded-full border border-[#2A2A2A] pt-1.5">
          <span className="scroll-cue-dot h-1.5 w-1.5 rounded-full bg-[#1F7A4D]" />
        </span>
      </motion.div>
    </section>
  );
}
